import { useForm } from 'antd/lib/form/Form';
import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  ButtonsWrapper,
  Form,
  Modal,
  PLATFORM_THEME_COLORS,
  Typography,
  TYPOGRAPHY_SIZE,
} from '@thinknet-gmbh/platform-common-ui';

import { WorkingConditions } from 'src/components/employee/working-conditions';
import { IWorkingConditions } from 'src/components/employee/employee.interfaces';
import { useUpdateActiveWorkingCondition } from 'src/hooks/working-conditions/update-active-working-condition.hook';
import { useUpdateInactiveWorkingCondition } from 'src/hooks/working-conditions/update-inactive-working-condition.hook';

import { HeaderContainer, ModalContent } from './working-conditions-edit.styles';
import isEmpty from 'lodash/isEmpty';

const WorkingEditionsEdit = ({
  onCancel,
  initialValues,
}: {
  onCancel: () => void;
  initialValues: any;
}) => {
  const { t } = useTranslation(['common', 'employee']);
  const [editedData, setEditedData] = useState<IWorkingConditions[] | null>(null);
  const [form] = useForm();

  const updateActiveWorkingCondition = useUpdateActiveWorkingCondition({ onSuccess: onCancel });
  const updateInactiveWorkingCondition = useUpdateInactiveWorkingCondition({ onSuccess: onCancel });

  const onFinish = (values: any) => {
    const { memberId, ...payload } = values;

    if (payload.active) {
      updateActiveWorkingCondition({
        variables: { workingConditionId: initialValues._id, payload },
      });
    } else {
      updateInactiveWorkingCondition({
        variables: { workingConditionId: initialValues._id, payload },
      });
    }
  };

  return (
    <div>
      <Modal
        isOpened={!!editedData}
        title={t('employee:workingConditions.terminationModal.title')}
        smallerPadding
        onClose={() => setEditedData(null)}
      >
        <ModalContent>
          <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
            {t('employee:workingConditions.terminationModal.subtitle')}
          </Typography>
          <ButtonsWrapper marginTop={14}>
            <Button
              type="primary"
              onClick={() => {
                onFinish(editedData || []);
                setEditedData(null);
              }}
            >
              {t('common:buttons.confirm')}
            </Button>
            <Button onClick={() => setEditedData(null)}>{t('common:buttons.cancel')}</Button>
          </ButtonsWrapper>
        </ModalContent>
      </Modal>
      <br />
      <HeaderContainer>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {t('employee:labels.editWorkingConditions')}
        </Typography>
      </HeaderContainer>
      <Form
        layout="vertical"
        onFinish={values => {
          const isTerminationDate =
            !values.terminationDate ||
            values.terminationDate.toISOString() ===
              moment(initialValues?.terminationDate).toISOString();

          if (!isTerminationDate) {
            return setEditedData(values);
          }
          onFinish(values);
        }}
        initialValues={{
          ...initialValues,
          contractExpirationDate: initialValues.contractExpirationDate
            ? moment(initialValues.contractExpirationDate)
            : undefined,
          terminationDate: initialValues?.terminationDate
            ? moment(initialValues.terminationDate)
            : undefined,
          probationDate: moment(initialValues.probationDate),
          contracts: !isEmpty(initialValues?.contracts)
            ? initialValues?.contracts.map((contract: any) => ({
                ...contract,
                hireDate: moment(contract.hireDate),
                ...(contract?.endDate && { endDate: moment(contract.endDate) }),
              }))
            : [{}],
        }}
        form={form}
      >
        <WorkingConditions isEdit form={form} />
        <ButtonsWrapper>
          <Button type="primary" htmlType="submit">
            {t('common:buttons.save')}
          </Button>
          <Button onClick={onCancel}>{t('common:buttons.cancel')}</Button>
        </ButtonsWrapper>
      </Form>
    </div>
  );
};

export default WorkingEditionsEdit;
